import {
  applyFormSubmissionFieldUpdateProposal as applyFormSubmissionFieldUpdateProposalApi,
  approveFormSubmissionFieldUpdateProposalChange as approveFormSubmissionFieldUpdateProposalChangeApi,
  clearFormSubmissionMatch as clearFormSubmissionMatchApi,
  confirmFormSubmissionMatch as confirmFormSubmissionMatchApi,
  discardFormSubmission as discardFormSubmissionApi,
  generateFormSubmissionFieldUpdateProposal as generateFormSubmissionFieldUpdateProposalApi,
  getFormSubmission as getFormSubmissionApi,
  listFormSubmissions as listFormSubmissionsApi,
  markFormSubmissionSpam as markFormSubmissionSpamApi,
  rejectFormSubmissionFieldUpdateProposalChange as rejectFormSubmissionFieldUpdateProposalChangeApi,
  rejectFormSubmissionFieldUpdateProposal as rejectFormSubmissionFieldUpdateProposalApi,
  restoreFormSubmission as restoreFormSubmissionApi,
} from "@shared/api/form-submissions";
import type {
  ConfirmSubmissionMatchRequest,
  FormSubmission,
  FormSubmissionQuery,
} from "@shared/types/form-submission";

interface ListState {
  data: FormSubmission[] | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({
  data: null,
  loading: false,
  error: null,
});
let listToken = 0;

export const formSubmissionListStore = {
  get data(): FormSubmission[] | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(query: FormSubmissionQuery): Promise<void> {
    const token = ++listToken;
    listState.loading = true;
    listState.error = null;
    try {
      const res = await listFormSubmissionsApi(query);
      if (token !== listToken) return;
      listState.data = res.items;
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
      listState.data = null;
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
  upsert(submission: FormSubmission): void {
    const current = listState.data ?? [];
    listState.data = current.map((item) =>
      item.id === submission.id ? submission : item,
    );
  },
  clear(): void {
    listToken++;
    listState.data = null;
    listState.error = null;
    listState.loading = false;
  },
};

interface DetailState {
  data: FormSubmission | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({
  data: null,
  loading: false,
  error: null,
});
let detailToken = 0;

export const formSubmissionDetailStore = {
  get data(): FormSubmission | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const submission = await getFormSubmissionApi(id);
      if (token !== detailToken) return;
      detailState.data = submission;
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(submission: FormSubmission): void {
    detailState.data = submission;
    formSubmissionListStore.upsert(submission);
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

export async function confirmFormSubmissionMatch(
  id: string,
  body: ConfirmSubmissionMatchRequest,
): Promise<FormSubmission> {
  const submission = await confirmFormSubmissionMatchApi(id, body);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function clearFormSubmissionMatch(
  id: string,
): Promise<FormSubmission> {
  const submission = await clearFormSubmissionMatchApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function markFormSubmissionSpam(
  id: string,
): Promise<FormSubmission> {
  const submission = await markFormSubmissionSpamApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function discardFormSubmission(
  id: string,
): Promise<FormSubmission> {
  const submission = await discardFormSubmissionApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function restoreFormSubmission(
  id: string,
): Promise<FormSubmission> {
  const submission = await restoreFormSubmissionApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function generateFormSubmissionFieldUpdateProposal(
  id: string,
): Promise<FormSubmission> {
  const submission = await generateFormSubmissionFieldUpdateProposalApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function applyFormSubmissionFieldUpdateProposal(
  id: string,
): Promise<FormSubmission> {
  const submission = await applyFormSubmissionFieldUpdateProposalApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function approveFormSubmissionFieldUpdateProposalChange(
  id: string,
  changeId: string,
): Promise<FormSubmission> {
  const submission = await approveFormSubmissionFieldUpdateProposalChangeApi(
    id,
    changeId,
  );
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function rejectFormSubmissionFieldUpdateProposalChange(
  id: string,
  changeId: string,
): Promise<FormSubmission> {
  const submission = await rejectFormSubmissionFieldUpdateProposalChangeApi(
    id,
    changeId,
  );
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}

export async function rejectFormSubmissionFieldUpdateProposal(
  id: string,
): Promise<FormSubmission> {
  const submission = await rejectFormSubmissionFieldUpdateProposalApi(id);
  formSubmissionDetailStore.setLocal(submission);
  return submission;
}
